import { useChat } from '@ai-sdk/react';
import { BaseModel, TargetableModel, Watchable } from '../actions/framework';
import { makeMessages, type Message } from './utils';
import { FileUIPart } from 'ai';
import type { PromptInputMessage } from '@cotera/client/v0/components/ai-elements/prompt-input';
import { DG } from '@cotera/sdk/core';

/** Status shown in the composer (`useChat` plus v1 duck-typed overrides on `as ChatModel`). */
export type ChatSurfaceStatus =
  | 'ready'
  | 'streaming'
  | 'submitted'
  | 'error'
  | 'loading';

export class ChatModel extends BaseModel implements TargetableModel {
  private _pendingToolCallResults: Record<string, any> = {};

  readonly chatControllerWatchable: Watchable<ReturnType<typeof useChat>>;
  readonly inputWatchable: Watchable<string>;
  readonly yoloModeWatchable: Watchable<boolean>;

  get messages(): Message[] {
    const rawMessages = this.chatController.messages;
    return makeMessages(rawMessages);
  }

  /**
   * Id of the last message, without materializing the full `messages` array.
   * `makeMessages` is a 1:1 map, so this equals `messages.at(-1)?.id` — read it
   * in render paths that only need to know which message is last (e.g. the
   * reasoning "is streaming" check) to avoid the O(N·P) `messages` rebuild.
   */
  get lastMessageId(): string | undefined {
    return this.chatController.messages.at(-1)?.id;
  }

  get chatController(): ReturnType<typeof useChat> {
    return this.chatControllerWatchable.snapshot();
  }

  constructor(
    id: string,
    chatController: ReturnType<typeof useChat>,
    readonly model: string,
    readonly parent: {
      isTargeted: boolean;
      isEqualTo: (other: unknown) => boolean;
      setChatController?: (chatController: ReturnType<typeof useChat>) => void;
    }
  ) {
    super(id);

    // Input state owned by ChatModel
    this.chatControllerWatchable = Watchable.fromValue(chatController);
    this.inputWatchable = Watchable.fromValue('');
    this.yoloModeWatchable = Watchable.fromValue(false);
  }

  setChatController(chatController: ReturnType<typeof useChat>) {
    this.chatControllerWatchable.set(chatController);
    this.parent.setChatController?.(chatController);
  }

  get status(): ChatSurfaceStatus {
    return (this.chatController?.status ?? 'ready') as ChatSurfaceStatus;
  }

  get yoloMode() {
    return this.yoloModeWatchable.snapshot();
  }

  setYoloMode(enabled: boolean) {
    this.yoloModeWatchable.set(enabled);
  }

  hasIncompleteToolCalls() {
    return this.messages.some(
      (x) => (x.toolInvocations?.incomplete().length ?? 0) > 0
    );
  }

  get isTargeted() {
    return this.parent.isTargeted;
  }

  target() {
    //noop - depends on parent
  }

  markAsNotTargeted() {
    //noop - depends on parent
  }

  addToolResult(toolCallId: string, result: any) {
    this._pendingToolCallResults[toolCallId] = result;
  }

  clearPendingToolResults() {
    this._pendingToolCallResults = {};
  }

  async clearChat() {
    await this.chatController.stop();
    this.chatController.clearError();
    this.chatController.setMessages([]);
    this.clearPendingToolResults();
    this.inputWatchable.set('');
  }

  get pendingToolCallResults() {
    return this._pendingToolCallResults;
  }

  async sendMessage(message: PromptInputMessage): Promise<void>;
  async sendMessage(text: string, files?: FileUIPart[]): Promise<void>;
  async sendMessage(
    textOrMessage: string | PromptInputMessage,
    files?: FileUIPart[]
  ): Promise<void> {
    let text: string;
    let fileParts: FileUIPart[] | undefined;
    if (typeof textOrMessage === 'string') {
      text = textOrMessage;
      fileParts = files;
    } else if (textOrMessage.type === 'text') {
      text = textOrMessage.text;
      fileParts = textOrMessage.files;
    } else {
      const editor = new DG.DocumentEditor.EditorState(
        textOrMessage.editorState as DG.DocumentEditor.DocumentState
      );
      text = editor.toPromptMarkdown(DG.BuildExplorer.empty(), 'simple');
      fileParts = textOrMessage.files;
    }

    const hasText = Boolean(text?.trim());
    const hasAttachments = Boolean(fileParts?.length);

    if (!(hasText || hasAttachments)) {
      return;
    }

    this.inputWatchable.set('');
    await this.chatController!.sendMessage({
      text: text?.trim() || 'Sent with attachments',
      files: fileParts,
    });
  }

  isEqualTo(other: ChatModel) {
    return this.id === other.id && this.parent.isEqualTo(other.parent);
  }
}
